import React, { FocusEvent, useRef, useState, useEffect, ChangeEvent } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, X, Check, Trash2 } from "lucide-react";
import { Badge } from "src/shadcn/ui/badge";
import { Input } from "src/shadcn/ui/input";
import { cn } from "src/lib/utils";

export interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface MultiSelectInputBoxProps {
  id: string;
  label?: string;
  values: string[];
  options: MultiSelectOption[] | string[];
  onChange?: (values: string[]) => void;
  onFocusChange?: (event: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  required?: boolean;
  error?: boolean | string;
  className?: string;
  size?: "sm" | "md" | "lg";
  placeholder?: string;
  showBadges?: boolean;
}

const MultiSelectInputBox: React.FC<MultiSelectInputBoxProps> = ({
  id,
  label,
  values = [],
  options,
  onChange,
  onFocusChange,
  onFocus,
  onBlur,
  disabled = false,
  required = false,
  error,
  className = "",
  size = "md",
  placeholder = "Select options...",
  showBadges = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const isMouseDownRef = useRef(false);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const hasError = typeof error === "string" ? !!error : !!error;
  const errorMessage = typeof error === "string" ? error : undefined;

  // Normalize options to MultiSelectOption format
  const normalizedOptions: MultiSelectOption[] = options.map((option) => {
    if (typeof option === "string") {
      return { value: option, label: option };
    }
    return option;
  });

  // Filter options based on input text
  const filteredOptions = normalizedOptions.filter((option) => {
    if (!inputText.trim()) return true;
    const searchText = inputText.toLowerCase();
    return option.label.toLowerCase().includes(searchText) || option.value.toLowerCase().includes(searchText);
  });

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const updatePosition = () => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          setPosition({
            top: rect.bottom,
            left: rect.left,
            width: rect.width
          });
        }
      };

      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const value = e.target.value;
    setInputText(value);
    setIsOpen(true);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (e.key === "Escape") {
      setIsOpen(false);
      setInputText("");
      inputRef.current?.blur();
    } else if (e.key === "Enter" && isOpen && filteredOptions.length > 0) {
      e.preventDefault();
      // Select the first non-selected option, or toggle if already selected
      const firstOption = filteredOptions[0];
      if (firstOption && !firstOption.disabled) {
        handleSelect(firstOption.value);
      }
    }
  };

  const handleInputMouseDown = () => {
    // Track that mouse is down to prevent blur from closing dropdown
    isMouseDownRef.current = true;
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Clear any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    if (!disabled) {
      setIsOpen(true);
      onFocusChange?.(e);
      onFocus?.(e);
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Clear any existing timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    // Use setTimeout to allow click events to process first
    blurTimeoutRef.current = setTimeout(() => {
      // Check if we should close the dropdown
      // Don't close if:
      // 1. Mouse is still down (user is clicking)
      // 2. Focus moved to dropdown items
      // 3. Focus is still within the container
      const shouldClose =
        !isMouseDownRef.current &&
        !containerRef.current?.contains(document.activeElement) &&
        !dropdownRef.current?.contains(document.activeElement);

      if (shouldClose) {
        setIsOpen(false);
        setInputText(""); // Clear input text on blur
        onFocusChange?.(e);
        onBlur?.(e);
      }

      // Reset mouse down flag after checking
      isMouseDownRef.current = false;
      blurTimeoutRef.current = null;
    }, 200);
  };

  const handleSelect = (optionValue: string) => {
    if (disabled) return;

    const newValues = values.includes(optionValue) ? values.filter((v) => v !== optionValue) : [...values, optionValue];

    onChange?.(newValues);
    setInputText(""); // Clear input text after selection
    inputRef.current?.focus(); // Keep focus on input
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      onChange?.([]);
      setIsOpen(false);
    }
  };

  const handleRemoveBadge = (valueToRemove: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      onChange?.(values.filter((v) => v !== valueToRemove));
    }
  };

  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] mt-1"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`
      }}
    >
      <div className="bg-white border border-secondary-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => {
            const isSelected = values.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                disabled={option.disabled || disabled}
                className={`w-full flex items-center gap-2 px-4 py-2 text-left text-sm transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg focus:outline-none ${
                  option.disabled || disabled
                    ? "text-secondary-400 cursor-not-allowed"
                    : isSelected
                      ? "bg-primary-100 text-primary-700"
                      : "text-secondary-700 hover:bg-primary-50 hover:text-primary-700"
                }`}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent input from losing focus
                  if (!option.disabled && !disabled) {
                    handleSelect(option.value);
                  }
                }}
              >
                <div
                  className={`flex items-center justify-center w-4 h-4 border-2 rounded transition-all flex-shrink-0 ${
                    isSelected ? "bg-primary-700 border-primary-700" : "border-secondary-300 hover:border-secondary-400"
                  }`}
                >
                  {isSelected && <Check className="h-3 w-3 text-white" />}
                </div>
                <span className="flex-1">{option.label}</span>
              </button>
            );
          })
        ) : (
          <div className="px-4 py-2 text-sm text-secondary-500 text-center">No options found</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={id} className="text-xs font-medium text-secondary-700 uppercase tracking-wide">
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}

      <div className="space-y-1.5">
        <div className="relative" ref={containerRef}>
          <div
            className={cn(
              "flex min-h-10 w-full rounded-md border bg-background px-3 py-2 text-base ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
              "flex-wrap gap-1.5 items-center",
              hasError
                ? "border-error-300 focus-within:ring-error-500 focus-within:border-error-500"
                : "border-input focus-within:ring-ring hover:border-secondary-400",
              disabled && "bg-secondary-50 cursor-not-allowed opacity-60",
              className
            )}
            onMouseDown={handleInputMouseDown}
          >
            {values.length > 0 &&
              values.map((value) => {
                const option = normalizedOptions.find((opt) => opt.value === value);
                return (
                  <Badge
                    key={value}
                    variant="secondary"
                    className="text-xs px-2 py-0.5 h-5 bg-secondary-100 text-secondary-900 hover:bg-secondary-200 flex items-center gap-1 flex-shrink-0"
                  >
                    {option?.label || value}
                    <button
                      type="button"
                      onClick={(e) => handleRemoveBadge(value, e)}
                      className="ml-1 hover:text-secondary-600 transition-colors"
                      tabIndex={-1}
                      disabled={disabled}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
            <Input
              ref={inputRef}
              id={id}
              type="text"
              value={inputText}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              disabled={disabled}
              placeholder={values.length === 0 ? placeholder : ""}
              className={cn(
                "flex-1 min-w-[120px] border-0 p-0 h-auto focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent",
                "placeholder:text-muted-foreground"
              )}
            />
          </div>
          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-1 items-center">
            {values.length > 0 && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-secondary-100 rounded transition-colors flex items-center justify-center"
                tabIndex={-1}
                disabled={disabled}
              >
                <Trash2 className="h-3.5 w-3.5 text-secondary-500" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="p-1 hover:bg-secondary-100 rounded transition-colors flex items-center justify-center"
              tabIndex={-1}
              disabled={disabled}
            >
              <ChevronDown
                className={`h-3.5 w-3.5 text-secondary-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
          </div>
          {dropdownContent && createPortal(dropdownContent, document.body)}
        </div>

        {errorMessage && <p className="text-sm text-error-600">{errorMessage}</p>}
      </div>
    </div>
  );
};

export { MultiSelectInputBox };
